import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@wbk/ui';
import { UpRightIcon } from '@wbk/svg';
import { WebSection } from '@wbk/contentful/api';
import { parseSectionItem } from '@wbk/contentful';
import { Link, useRouter } from '@wbk/config';
import { useAnalytics } from '@wbk/analytics';
import { formatCustomDate } from '@/utils';
import { getEventRedirectUrl } from '@/utils/redirect';
import EventCard from '../../events/EventCard';
import ScrollTitle from '../../ui/ScrollTitle';
import type { ContentfulMedia as ContentfulMediaType } from '@wbk/contentful/api';

export type WebItem = {
  label: undefined;
  id: string;
  href: string;
  title: string;
  startingPrice: number;
  currencyCode: string;
  image11: ContentfulMediaType;
  schedule?: {
    openDateTime: string;
  };
  zone?: {
    zoneLogo: ContentfulMediaType;
  };
};

export default function EventSection({
  section,
  showSeeMore = true,
}: {
  section: WebSection;
  showSeeMore?: boolean;
}) {
  const { locale } = useRouter();

  const { trackEvent } = useAnalytics();

  const items = section.contentCollection.items;
  const linkItem = section?.webLinksCollection?.items?.[0];

  const { t } = useTranslation('common');

  return (
    <section className='container-padding container space-y-12'>
      <div className='container hidden px-3 lg:block lg:space-y-12 xl:px-0'>
        <div className='flex items-center justify-between'>
          <ScrollTitle title={section.title} />

          <div className='hidden gap-1'>
            <Button
              phill
              className='flex h-fit items-center gap-2 border border-[#52525B] bg-transparent px-3 py-2'
            >
              <Typography variant='body-XS' className=''>
                Gaming
              </Typography>
            </Button>
            <Button
              phill
              className='flex h-fit items-center gap-2 border border-[#52525B] bg-transparent px-3 py-2'
            >
              <Typography variant='body-XS' className=''>
                Theater
              </Typography>
            </Button>
            <Button
              phill
              className='flex h-fit items-center gap-2 border border-[#52525B] bg-transparent px-3 py-2'
            >
              <Typography variant='body-XS' className=''>
                Adventure
              </Typography>
            </Button>
            <Button
              phill
              className='flex h-fit items-center gap-2 border border-[#52525B] bg-transparent px-3 py-2'
            >
              <Typography variant='body-XS' className=''>
                Sports
              </Typography>
            </Button>
          </div>
        </div>

        <div className='space-y-8'>
          <div className='flex justify-between gap-4 xl:gap-8'>
            {items.slice(0, 3).map((item, index) => {
              const parsedItem = parseSectionItem({ item, locale });
              if (!parsedItem) return null;
              const parsed = parsedItem as WebItem;

              const redirectInfo = getEventRedirectUrl(
                item,
                'slug' in item ? item.slug : '',
                locale,
                parsed
              );

              const openDate = parsed?.schedule?.openDateTime;

              return (
                <Button
                  as={Link}
                  href={redirectInfo.href}
                  theme='white'
                  shape='text'
                  className='h-full w-full p-0'
                  key={index}
                  target={redirectInfo.isExternal ? '_blank' : undefined}
                  onClick={() => {
                    trackEvent({
                      event: 'clicked_on_section_item',
                      data: {
                        type: item.__typename,
                        slug: 'slug' in item ? item.slug : '',
                        section: section.id,
                        position: index,
                      },
                    });
                  }}
                  rel='noreferrer'
                >
                  <EventCard
                    key={index}
                    eventType={parsedItem?.eventType}
                    venueName={parsed.title}
                    eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
                    priceFrom={parsed.startingPrice}
                    currency={parsed.currencyCode}
                    image={parsed.image11}
                    zoneLogo={parsed.zone?.zoneLogo}
                    buttonText={parsed?.label || undefined}
                  />
                </Button>
              );
            })}
          </div>
          <div className='flex justify-between gap-4 xl:gap-8'>
            {items.slice(3, 7).map((item, index) => {
              const parsedItem = parseSectionItem({ item, locale });
              if (!parsedItem) return null;
              const parsed = parsedItem as WebItem;

              const redirectInfo = getEventRedirectUrl(
                item,
                'slug' in item ? item.slug : '',
                locale,
                parsed
              );

              const openDate = parsed?.schedule?.openDateTime;

              return (
                <Button
                  as={Link}
                  href={redirectInfo.href}
                  theme='white'
                  shape='text'
                  className='h-full w-full p-0'
                  key={index}
                  target={redirectInfo.isExternal ? '_blank' : undefined}
                  onClick={() => {
                    trackEvent({
                      event: 'clicked_on_section_item',
                      data: {
                        type: item.__typename,
                        slug: 'slug' in item ? item.slug : '',
                        section: section.id,
                        position: index,
                      },
                    });
                  }}
                  rel='noreferrer'
                >
                  <EventCard
                    key={index}
                    venueName={parsed.title}
                    eventType={parsedItem?.eventType}
                    eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
                    priceFrom={parsed.startingPrice}
                    currency={parsed.currencyCode}
                    image={parsed.image11}
                    zoneLogo={parsed.zone?.zoneLogo}
                    buttonText={parsed?.label || undefined}
                    // onButtonClick={handleGetTickets}
                  />
                </Button>
              );
            })}
          </div>
          <div className='flex justify-between gap-4 xl:gap-8'>
            {items.slice(7, 11).map((item, index) => {
              const parsedItem = parseSectionItem({ item, locale });
              if (!parsedItem) return null;
              const parsed = parsedItem as WebItem;

              const redirectInfo = getEventRedirectUrl(
                item,
                'slug' in item ? item.slug : '',
                locale,
                parsed
              );

              const openDate = parsed?.schedule?.openDateTime;

              return (
                <Button
                  as={Link}
                  href={redirectInfo.href}
                  theme='white'
                  shape='text'
                  className='h-full w-full p-0'
                  key={index}
                  target={redirectInfo.isExternal ? '_blank' : undefined}
                  onClick={() => {
                    trackEvent({
                      event: 'clicked_on_section_item',
                      data: {
                        type: item.__typename,
                        slug: 'slug' in item ? item.slug : '',
                        section: section.id,
                        position: index,
                      },
                    });
                  }}
                  rel='noreferrer'
                >
                  <EventCard
                    key={index}
                    venueName={parsed.title}
                    eventType={parsedItem?.eventType}
                    eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
                    priceFrom={parsed.startingPrice}
                    currency={parsed.currencyCode}
                    image={parsed.image11}
                    zoneLogo={parsed.zone?.zoneLogo}
                    buttonText={parsed?.label || undefined}
                    // onButtonClick={handleGetTickets}
                  />
                </Button>
              );
            })}
          </div>

          {showSeeMore && (
            <div className='flex'>
              <Button
                phill
                as={Link}
                href={`/${locale}/explore`}
                className='mx-auto flex items-center border border-white bg-transparent capitalize'
              >
                <Typography variant='body-L' className='font-semibold'>
                  {linkItem?.label || t('see_all')}
                </Typography>
                <UpRightIcon />
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className='container space-y-6 px-3 md:space-y-12 lg:hidden'>
        <div className='flex flex-col gap-4'>
          <ScrollTitle title={section.title} />

          <div className='hidden gap-2'>
            <Button
              phill
              className='flex h-fit items-center gap-2 border border-[#52525B] bg-transparent px-3 py-2'
            >
              <Typography variant='body-XS' className=''>
                Gaming
              </Typography>
            </Button>
            <Button
              phill
              className='flex h-fit items-center gap-2 border border-[#52525B] bg-transparent px-3 py-2'
            >
              <Typography variant='body-XS' className=''>
                Theater
              </Typography>
            </Button>
            <Button
              phill
              className='flex h-fit items-center gap-2 border border-[#52525B] bg-transparent px-3 py-2'
            >
              <Typography variant='body-XS' className=''>
                Adventure
              </Typography>
            </Button>
            <Button
              phill
              className='flex h-fit items-center gap-2 border border-[#52525B] bg-transparent px-3 py-2'
            >
              <Typography variant='body-XS' className=''>
                Sports
              </Typography>
            </Button>
          </div>
        </div>
        {items.slice(0, 1).map((item, index) => {
          const parsedItem = parseSectionItem({ item, locale });
          if (!parsedItem) return null;
          const parsed = parsedItem as WebItem;

          const redirectInfo = getEventRedirectUrl(
            item,
            'slug' in item ? item.slug : '',
            locale,
            parsed
          );

          const openDate = parsed?.schedule?.openDateTime;

          return (
            <Button
              as={Link}
              href={redirectInfo.href}
              theme='white'
              shape='text'
              className='h-full w-full p-0'
              key={index}
              target={redirectInfo.isExternal ? '_blank' : undefined}
              onClick={() => {
                trackEvent({
                  event: 'clicked_on_section_item',
                  data: {
                    type: item.__typename,
                    slug: 'slug' in item ? item.slug : '',
                    section: section.id,
                    position: index,
                  },
                });
              }}
              rel='noreferrer'
            >
              <EventCard
                key={index}
                venueName={parsed.title}
                eventType={parsedItem?.eventType}
                eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
                priceFrom={parsed.startingPrice}
                currency={parsed.currencyCode}
                image={parsed.image11}
                zoneLogo={parsed.zone?.zoneLogo}
                buttonContainerClassName='!flex-row'
                buttonText={parsed?.label || undefined}
                // onButtonClick={handleGetTickets}
              />
            </Button>
          );
        })}
        <div className='grid grid-cols-2 gap-4'>
          {items.slice(1, 7).map((item, index) => {
            const parsedItem = parseSectionItem({ item, locale });
            if (!parsedItem) return null;
            const parsed = parsedItem as WebItem;

            const redirectInfo = getEventRedirectUrl(
              item,
              'slug' in item ? item.slug : '',
              locale,
              parsed
            );

            const openDate = parsed?.schedule?.openDateTime;

            return (
              <Button
                as={Link}
                href={redirectInfo.href}
                theme='white'
                shape='text'
                className='h-full w-full p-0'
                key={index}
                target={redirectInfo.isExternal ? '_blank' : undefined}
                onClick={() => {
                  trackEvent({
                    event: 'clicked_on_section_item',
                    data: {
                      type: item.__typename,
                      slug: 'slug' in item ? item.slug : '',
                      section: section.id,
                      position: index,
                    },
                  });
                }}
                rel='noreferrer'
              >
                <EventCard
                  key={index}
                  venueName={parsed.title}
                  eventType={parsedItem?.eventType}
                  eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
                  priceFrom={parsed.startingPrice}
                  currency={parsed.currencyCode}
                  image={parsed.image11}
                  zoneLogo={parsed.zone?.zoneLogo}
                  buttonText={parsed?.label || undefined}
                />
              </Button>
            );
          })}
        </div>

        {showSeeMore && (
          <div className='flex'>
            <Button
              phill
              as={Link}
              href={`/${locale}/explore`}
              className='mx-auto flex items-center border border-white bg-transparent'
            >
              <Typography variant='body-S' className='sm:text-body-L font-semibold'>
                {linkItem?.label || t('see_all')}
              </Typography>
              <UpRightIcon />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
